<template>
  <modal name="securone-upload-document" class="modal-inner" transition="pop-out" :width="620" height="auto" :adaptive="true" :scrollable="true" :focus-trap="true" @before-close="reset">
    <span class="close-button" @click="$modal.hide('securone-upload-document')"><BaseIcon icon="times-circle" class="text-white" /></span>
    <div class="modal-header text-lg font-serif">{{ mode === "version" ? $t("securone.documents.new_version_title", { name: document ? document.title : "" }) : $t("securone.documents.upload_title") }}</div>
    <div class="modal-body">
      <div class="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">{{ $t("securone.documents.file") }}</div>
      <label class="flex items-center gap-3 rounded border border-dashed border-secondary-300 bg-gray-50 px-4 py-3 cursor-pointer hover:border-primary-400">
        <BaseIcon icon="file-alt" class="text-gray-400 text-lg" />
        <div class="flex-1 min-w-0">
          <div v-if="file" class="text-sm text-gray-800 truncate">{{ file.name }}</div>
          <div v-else class="text-sm text-gray-500">{{ $t("securone.documents.choose_file") }}</div>
          <div class="text-xs text-gray-400">{{ file ? fileSize(file.size) + " · " + (file.type || "") : $t("securone.documents.allowed_types") }}</div>
        </div>
        <span class="text-xs text-primary-500">{{ file ? $t("securone.documents.replace") : $t("securone.documents.browse") }}</span>
        <input type="file" class="hidden" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.txt" @change="onFile" />
      </label>

      <ValidationObserver ref="form" v-if="mode === 'upload'">
        <div class="py-2 mt-2"><BaseInput v-model="form.title" :field_name="$t('securone.documents.name')" rules="required" /></div>
        <div class="text-xs text-gray-500 -mt-1 mb-3">{{ $t("securone.documents.file_name_not_shown") }}</div>
        <LevelPicker v-model="level" />
        <div class="text-xs text-gray-500 mt-3">{{ $t("securone.documents.attach_under_categories") }}</div>
      </ValidationObserver>

      <SoNote v-if="mode === 'version' && document" tone="info" class="mt-3">{{ $t("securone.documents.new_version_note", { next: document.currentVersion + 1, current: document.currentVersion }) }}</SoNote>
      <SoNote v-if="error" tone="bad" class="mt-3">{{ error }}</SoNote>
    </div>
    <div class="modal-footer">
      <div class="footer-container clearfix">
        <button class="sd-btn sd-btn--secondary float-left" @click="$modal.hide('securone-upload-document')">{{ $t("securone.common.cancel") }}<BaseIcon icon="times-circle" class="ml-1" /></button>
        <button class="sd-btn sd-btn--success float-right" :disabled="busy || !file" @click="submit">
          {{ mode === "version" ? $t("securone.documents.publish_version", { n: document ? document.currentVersion + 1 : "" }) : $t("securone.documents.upload") }}<BaseIcon icon="upload" class="ml-1" />
        </button>
      </div>
    </div>
  </modal>
</template>

<script>
import SoNote from "./SoNote.vue";
import LevelPicker from "./LevelPicker.vue";
import api from "../securone.api";
import { errorMessage, fileSize } from "../securone.helpers";

// D-34 (upload) and "New version" from D-23. A new version keeps the document
// id (I4); the categories attached to it send the new version from now on.
export default {
  name: "UploadDocumentModal",
  components: { SoNote, LevelPicker },
  data() {
    return { mode: "upload", document: null, file: null, form: { title: "" }, level: { level: "departments", departmentIds: [] }, busy: false, error: null };
  },
  methods: {
    fileSize,
    openUpload() {
      this.reset();
      this.mode = "upload";
      this.document = null;
      this.$modal.show("securone-upload-document");
    },
    openVersion(document) {
      this.reset();
      this.mode = "version";
      this.document = document;
      this.$modal.show("securone-upload-document");
    },
    reset() {
      this.file = null;
      this.form = { title: "" };
      this.level = { level: this.$store.getters["securone/canWrite"].allUsersDocument ? "all_users" : "departments", departmentIds: [] };
      this.busy = false;
      this.error = null;
    },
    onFile(e) {
      const file = e.target.files && e.target.files[0];
      this.file = file || null;
      if (file && this.mode === "upload" && !this.form.title) this.form.title = file.name.replace(/\.[^.]+$/, "");
    },
    async submit() {
      if (!this.file) return;
      if (this.mode === "upload") {
        if (!(await this.$refs.form.validate())) return;
        if (this.level.level === "departments" && !this.level.departmentIds.length) {
          this.error = this.$t("securone.department.required");
          return;
        }
      }
      this.busy = true;
      this.error = null;
      try {
        const result = this.mode === "version"
          ? await api.addDocumentVersion(this.document._id, this.file)
          : await api.uploadDocument({ title: this.form.title, level: this.level.level, departmentIds: this.level.departmentIds }, this.file);
        this.$modal.hide("securone-upload-document");
        this.$emit("saved", result);
      } catch (error) {
        this.error = errorMessage(this, error, "securone.errors.upload_failed");
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>
